import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Stack,
  Divider,
} from '@mui/material';
import {
  Backup as BackupIcon,
  Refresh as RefreshIcon,
  PlayArrow as RunIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../../utils/api';

function statusColor(status) {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'running') return 'warning';
  return 'default';
}

function formatBytes(n) {
  if (n == null || Number.isNaN(n)) return '—';
  if (n < 1024) return `${n} B`;
  const mb = n / (1024 * 1024);
  if (mb < 1024) return `${mb.toFixed(2)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

export default function DbBackupAdmin() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [config, setConfig] = useState({ retentionMax: 10, intervalMinutes: 60, hourlyEnabled: true });
  const [savingConfig, setSavingConfig] = useState(false);
  const [runningBackup, setRunningBackup] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailJob, setDetailJob] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [catalogEntryOpen, setCatalogEntryOpen] = useState(null);
  const [requestOpen, setRequestOpen] = useState(null);
  const [deletionReason, setDeletionReason] = useState('');
  const [submittingRequest, setSubmittingRequest] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [st, j, cat] = await Promise.all([
        api.get('/admin/db-backup/status'),
        api.get('/admin/db-backup/jobs'),
        api.get('/admin/db-backup/catalog').catch(() => ({ data: null })),
      ]);
      setStatus(st.data?.data ?? null);
      if (st.data?.data?.config) {
        setConfig((c) => ({ ...c, ...st.data.data.config }));
      }
      setJobs(Array.isArray(j.data?.data) ? j.data.data : []);
      if (cat?.data?.success && cat.data?.data) {
        setCatalog(cat.data.data);
      } else {
        setCatalog(null);
      }
    } catch (e) {
      const msg =
        e.response?.data?.message ||
        e.message ||
        'Failed to load backup service (check API → erp4c-database-backup connectivity)';
      toast.error(msg, { autoClose: 12000 });
      setStatus(null);
      setCatalog(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await api.put('/admin/db-backup/config', {
        retentionMax: Number(config.retentionMax),
        intervalMinutes: Number(config.intervalMinutes),
        hourlyEnabled: Boolean(config.hourlyEnabled),
      });
      toast.success('Backup settings saved');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    } finally {
      setSavingConfig(false);
    }
  };

  const runNow = async () => {
    setRunningBackup(true);
    try {
      const r = await api.post('/admin/db-backup/run', {});
      toast.success(r.data?.message || 'Backup finished');
      loadAll();
    } catch (e) {
      const msg = e.response?.data?.message || e.message || 'Backup failed';
      toast.error(msg);
      loadAll();
    } finally {
      setRunningBackup(false);
    }
  };

  const openDetail = async (id) => {
    setDetailOpen(true);
    setDetailJob(null);
    setDetailLoading(true);
    try {
      const r = await api.get(`/admin/db-backup/jobs/${encodeURIComponent(id)}`);
      setDetailJob(r.data?.data || null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not load job');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const submitDeletionRequest = async () => {
    if (!requestOpen?.folderName) return;
    const reason = deletionReason.trim();
    if (reason.length < 10) {
      toast.error('Reason must be at least 10 characters.');
      return;
    }
    setSubmittingRequest(true);
    try {
      await api.post('/admin/db-backup/deletion-requests', {
        folder_name: requestOpen.folderName,
        reason,
      });
      toast.success('Deletion request submitted. Another administrator must approve it under All Statuses.');
      setRequestOpen(null);
      setDeletionReason('');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Request failed');
    } finally {
      setSubmittingRequest(false);
    }
  };

  const serviceDown = status == null && !loading;
  const running = status?.runningJob || jobs.some((j) => j.status === 'running');

  return (
    <Box sx={{ p: 2, maxWidth: 1200, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <BackupIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          DB Backup Administration
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<RefreshIcon />} onClick={loadAll} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2" component="span" display="block" sx={{ mb: 1 }}>
          <strong>All backup work runs inside the erp4c-database-backup container/pod.</strong> It runs{' '}
          <code>pg_dump</code>, keeps the schedule, enforces retention, and records job history. The main ERP backend only
          proxies this screen and your actions to that service—it does not perform dumps itself.
        </Typography>
        <Typography variant="body2" component="span" display="block" sx={{ mb: 1 }}>
          <strong>The three most recent backup folders are always protected</strong> and cannot be deleted. Removing any
          older folder requires submitting a reason; a <strong>different</strong> administrator must approve under{' '}
          <Link component={RouterLink} to="/admin/all-statuses/db-backup-deletion">
            All Statuses → DB Backup Deletion Requests
          </Link>
          .
        </Typography>
        <Typography variant="body2" component="span" display="block">
          Turning <strong>automatic scheduled backups</strong> off stops the timer only; the container/pod{' '}
          <strong>keeps running</strong>. Admins can still use <strong>Run backup now</strong> for a one-off dump anytime.
        </Typography>
      </Alert>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        On the host, dumps should live under <code>ERPForC/backup/dbbackup</code> (mounted into the backup worker as{' '}
        <code>/backups</code>). Each run creates a folder <code>erp4c-dbbackup-{'{dd-mmm-yyyy-HH-mm-ss}'}</code> with{' '}
        <code>backup.dump</code> and a manifest.
      </Typography>

      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {!loading && serviceDown && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" component="div" sx={{ mb: 1 }}>
            The API could not load the backup worker (HTTP 502/503 or network error). Typical fixes:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            <Typography component="li" variant="body2">
              <strong>Local Kubernetes:</strong> enable <code>databaseBackup</code> in Helm values
              (<code>environments/docker-desktop-k8s-local/</code>) and ensure the backend has{' '}
              <code>DB_BACKUP_SERVICE_URL</code> pointing at the backup Service in namespace{' '}
              <code>erp4c-base</code>.
            </Typography>
            <Typography component="li" variant="body2">
              <strong>Azure / AKS:</strong> same Helm flag; confirm the backend env points at the in-cluster backup Service.
            </Typography>
          </Box>
        </Alert>
      )}

      {!loading && status && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              sx={(t) => ({
                bgcolor: alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.12 : 0.06),
              })}
            >
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Backup worker status
                </Typography>
                <Chip size="small" color="success" label="erp4c-database-backup reachable" sx={{ mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Target database: {status.database} @ {status.host}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Backup folders on disk: {status.backupFolderCount ?? '—'}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {running ? (
                    <Chip size="small" color="warning" label="Backup in progress" />
                  ) : (
                    <Chip size="small" color="success" label="Idle" />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              sx={(t) => ({
                bgcolor: alpha(t.palette.secondary.main, t.palette.mode === 'dark' ? 0.12 : 0.06),
              })}
            >
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Schedule & retention (stored in backup worker)
                </Typography>
                <Stack spacing={1.5}>
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(config.hourlyEnabled)}
                          onChange={(e) => setConfig((c) => ({ ...c, hourlyEnabled: e.target.checked }))}
                        />
                      }
                      label="Automatic scheduled backups"
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 0.5 }}>
                      Off = no timer; pod/container still runs. Use “Run backup now” for manual jobs.
                    </Typography>
                  </Box>
                  <TextField
                    size="small"
                    type="number"
                    label="Interval (minutes)"
                    value={config.intervalMinutes}
                    onChange={(e) => setConfig((c) => ({ ...c, intervalMinutes: e.target.value }))}
                    inputProps={{ min: 5, max: 1440 }}
                    helperText="Used only when automatic backups are on (e.g. 60 = hourly)"
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Backups to keep"
                    value={config.retentionMax}
                    onChange={(e) => setConfig((c) => ({ ...c, retentionMax: e.target.value }))}
                    inputProps={{ min: 1, max: 500 }}
                    helperText="Older backup folders are removed after each successful run (e.g. 3 = keep latest 3)"
                  />
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<SaveIcon />}
                      onClick={saveConfig}
                      disabled={savingConfig}
                    >
                      Save settings
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      startIcon={runningBackup ? <CircularProgress size={16} /> : <RunIcon />}
                      onClick={runNow}
                      disabled={runningBackup || running}
                    >
                      Run backup now
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {!loading && status && catalog && Array.isArray(catalog.entries) && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              On-disk backup catalog
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Root on worker: <code>{catalog.backupRoot || '—'}</code> — at least{' '}
              <strong>{catalog.minProtectedBackups ?? 3}</strong> newest folders are protected from deletion.
            </Typography>
            {catalog.entries.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No backup folders found yet.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Folder</TableCell>
                    <TableCell>Protection</TableCell>
                    <TableCell>Dump size</TableCell>
                    <TableCell>Dump modified</TableCell>
                    <TableCell>Files</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {catalog.entries.map((e) => (
                    <TableRow key={e.folderName} hover>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, maxWidth: 220, wordBreak: 'break-all' }}>
                        {e.folderName}
                      </TableCell>
                      <TableCell>
                        {e.isProtected ? (
                          <Chip size="small" color="primary" label="Protected (newest)" />
                        ) : (
                          <Chip size="small" variant="outlined" label="May request delete" />
                        )}
                      </TableCell>
                      <TableCell>{formatBytes(e.dumpSizeBytes)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {e.dumpMtime ? new Date(e.dumpMtime).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell>
                        manifest {e.filesPresent?.manifest ? '✓' : '—'} / dump {e.filesPresent?.dump ? '✓' : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => setCatalogEntryOpen(e)}>
                          All details
                        </Button>
                        {e.job?.id && (
                          <Button size="small" onClick={() => openDetail(e.job.id)}>
                            Job
                          </Button>
                        )}
                        {!e.isProtected && (
                          <Button
                            size="small"
                            color="warning"
                            onClick={() => {
                              setDeletionReason('');
                              setRequestOpen({ folderName: e.folderName });
                            }}
                          >
                            Request deletion
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {!loading && status && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Backup jobs
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Jobs are created only inside the backup worker: <strong>scheduled</strong> (timer) or <strong>manual</strong>{' '}
              (“Run backup now”). History is persisted there.
            </Typography>
            {jobs.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No jobs recorded yet.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Started</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Folder / file</TableCell>
                    <TableCell align="right">Size</TableCell>
                    <TableCell align="right"> </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.map((j) => (
                    <TableRow key={j.id} hover>
                      <TableCell>{j.startedAt ? new Date(j.startedAt).toLocaleString() : '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={j.type === 'manual' ? 'Manual' : 'Scheduled'} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={j.status} color={statusColor(j.status)} />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280, wordBreak: 'break-all' }}>
                        {j.folderName || '—'}
                        {j.status === 'completed' && j.backupFile ? (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {j.backupFile}
                          </Typography>
                        ) : null}
                        {j.errorMessage ? (
                          <Typography variant="caption" display="block" color="error">
                            {j.errorMessage}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">
                        {j.sizeBytes != null ? `${(j.sizeBytes / (1024 * 1024)).toFixed(2)} MB` : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => openDetail(j.id)}>
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Job details</DialogTitle>
        <DialogContent dividers>
          {detailLoading && <CircularProgress size={28} />}
          {!detailLoading && detailJob && (
            <Stack spacing={1}>
              <Typography variant="body2">
                <strong>Id:</strong> {detailJob.id}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {detailJob.status}
              </Typography>
              <Typography variant="body2">
                <strong>Type:</strong> {detailJob.type}
              </Typography>
              <Typography variant="body2">
                <strong>Started:</strong> {detailJob.startedAt}
              </Typography>
              <Typography variant="body2">
                <strong>Finished:</strong> {detailJob.finishedAt || '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Folder:</strong> {detailJob.folderName || '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Database:</strong> {detailJob.database} @ {detailJob.host}
              </Typography>
              {detailJob.pgDumpVersion && (
                <Typography variant="body2">
                  <strong>pg_dump:</strong> {detailJob.pgDumpVersion}
                </Typography>
              )}
              {detailJob.sizeBytes != null && (
                <Typography variant="body2">
                  <strong>Size:</strong> {(detailJob.sizeBytes / (1024 * 1024)).toFixed(2)} MB
                </Typography>
              )}
              {detailJob.errorMessage && (
                <Alert severity="error">{detailJob.errorMessage}</Alert>
              )}
              {detailJob.log?.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2">Log</Typography>
                  <Box component="pre" sx={{ fontSize: 12, overflow: 'auto', maxHeight: 200, m: 0 }}>
                    {detailJob.log.join('\n')}
                  </Box>
                </>
              )}
              {detailJob.manifest && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2">Manifest (on disk)</Typography>
                  <Box component="pre" sx={{ fontSize: 12, overflow: 'auto', maxHeight: 200, m: 0 }}>
                    {JSON.stringify(detailJob.manifest, null, 2)}
                  </Box>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(catalogEntryOpen)}
        onClose={() => setCatalogEntryOpen(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Backup folder details</DialogTitle>
        <DialogContent dividers>
          {catalogEntryOpen && (
            <Stack spacing={1}>
              <Typography variant="body2">
                <strong>Folder:</strong> {catalogEntryOpen.folderName}
              </Typography>
              <Typography variant="body2">
                <strong>Path on worker:</strong> {catalogEntryOpen.pathOnDisk || '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Directory mtime:</strong>{' '}
                {catalogEntryOpen.directoryMtime
                  ? new Date(catalogEntryOpen.directoryMtime).toLocaleString()
                  : '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Protected:</strong> {catalogEntryOpen.isProtected ? 'Yes (among newest)' : 'No'}
              </Typography>
              <Typography variant="body2">
                <strong>Dump file:</strong> {catalogEntryOpen.dumpFile || '—'} ({formatBytes(catalogEntryOpen.dumpSizeBytes)})
              </Typography>
              <Typography variant="body2">
                <strong>Dump mtime:</strong>{' '}
                {catalogEntryOpen.dumpMtime ? new Date(catalogEntryOpen.dumpMtime).toLocaleString() : '—'}
              </Typography>
              {catalogEntryOpen.job && (
                <Typography variant="body2">
                  <strong>Linked job:</strong> {catalogEntryOpen.job.id} ({catalogEntryOpen.job.type}) —{' '}
                  {catalogEntryOpen.job.status}
                </Typography>
              )}
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2">Manifest JSON</Typography>
              <Box component="pre" sx={{ fontSize: 12, overflow: 'auto', maxHeight: 240, m: 0 }}>
                {catalogEntryOpen.manifest
                  ? JSON.stringify(catalogEntryOpen.manifest, null, 2)
                  : '(no manifest file)'}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCatalogEntryOpen(null)}>Close</Button>
          {catalogEntryOpen && !catalogEntryOpen.isProtected && (
            <Button
              color="warning"
              onClick={() => {
                setRequestOpen({ folderName: catalogEntryOpen.folderName });
                setDeletionReason('');
              }}
            >
              Request deletion
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(requestOpen)} onClose={() => setRequestOpen(null)} fullWidth maxWidth="sm">
        <DialogTitle>Request backup folder deletion</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Folder: <strong>{requestOpen?.folderName}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Another administrator must approve. You cannot approve your own request.
          </Typography>
          <TextField
            label="Reason (min. 10 characters)"
            fullWidth
            multiline
            minRows={3}
            value={deletionReason}
            onChange={(e) => setDeletionReason(e.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestOpen(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={submitDeletionRequest} disabled={submittingRequest}>
            Submit request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
